// eslint-disable-next-line import/no-named-as-default
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import Dotdotdot from 'react-dotdotdot';

import {Panel} from '@stubhub/bamboo';
// eslint-disable-next-line import/no-named-as-default
import DateStamp from '@stubhub/react-date-stamp';
import {getEventTime, getEventDate, getVenueInfo} from '@stubhub/react-event-helper';
import FormattedLink from '@stubhub/react-formatted-link';
import {connect} from '@stubhub/react-store-provider';
import {theme} from '@stubhub/react-theme-provider';
import UKRedirectModal from '@stubhub/react-uk-redirect-modal';
import urgency from '@stubhub/react-urgency-decorator';

import './index.scss';

// Cxs is mandatory here, cannot test branch coverage
/* istanbul ignore next */
const themeLinkColorClassName = (theme = {}, cxs) =>
  theme.keyTextLinks &&
  cxs &&
  cxs({
    color: `${theme.keyTextLinks} !important`,
    fontWeight: 500,
  });

// Cxs is mandatory here, cannot test branch coverage
/* istanbul ignore next */
const themeHoverColorClassName = (theme = {}, cxs) =>
  theme.paletteFocus &&
  cxs &&
  cxs({
    ':hover': {
      background: `${theme.paletteFocus} !important`,
    },
  });

function mapStateToProps(state) {
  let deviceState = state.device || {};

  return {
    deviceType: deviceState.type,
  };
}

@theme
@urgency
class EventItem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      triggerUKRedirectModal: false,
    };
  }

  static propTypes = {
    theme: PropTypes.object,
    event: PropTypes.object,
    sellFlowUrl: PropTypes.string.isRequired,

    /**
     * ThemeProvider object for applying the theme.
     *
     */
    themeProvider: PropTypes.func.isRequired,

    /**
     * Options Containing the Parts of the Event Items to be Hidden
     *
     */
    eventItemDisplayOptions: PropTypes.shape({
      disableDateStamp: PropTypes.bool,
      className: PropTypes.string,
    }),
  };

  static defaultPropTypes = {
    eventItemDisplayOptions: {
      disableDateStamp: false,
      className: '',
    },
  };

  static contextTypes = {
    urgency: PropTypes.object,
  };

  /**
   * Function to Handle Click on Venue Link
   * @param evt React Event Object for the `click` action.
   */
  navigateToVenuePage = (evt) => {
    /* istanbul ignore if */
    if (evt) {
      evt.stopPropagation();
    }
  };

  navigateTo = (e) => {
    e && e.stopPropagation();
    e && e.preventDefault();
    let {isSell, isMultipleEventToggleSelected, event} = this.props;
    const isUKRedirectEnabled = parseInt(process.env.REACT_APP_UK_REDIRECT_ENABLED) === 1;
    const isUserDomainCoUK = window?.location?.hostname.indexOf('.co.uk') > -1;
    const showUKRedirectModal = isUKRedirectEnabled && !isUserDomainCoUK && event?.venue?.country === 'GB';
    if (showUKRedirectModal) {
      this.setState({triggerUKRedirectModal: true});

      return;
    }
    if (isSell && !isMultipleEventToggleSelected) {
      let links = this.mainNode.getElementsByClassName('EventItem__TitleLink');
      if (links && links[0]) {
        links[0].click();
      }
    }
  };

  render() {
    const {
      event,
      theme,
      themeProvider,
      eventItemDisplayOptions = {},
      isMultipleEventToggleSelected = false,
      onEventItemCheckboxClick = () => {
        //
      },
      isMultiCheckboxChecked = false,
      isSell,
      sellFlowUrl,
    } = this.props;
    const {triggerUKRedirectModal} = this.state;

    /**
     * Event Item Display Options to Control the Visibility and Styling of the Event item.
     *
     */
    const {disableDateStamp, className: eventItemClassName, hideBorder} = eventItemDisplayOptions;
    const {name, isDisabled = false} = event;

    /**
     * Event Date and Time Section
     *
     */
    const eventDate = getEventDate(event);
    const timeDisplay = getEventTime(event);

    /**
     * Event Venue Info
     *
     */
    const {venueName} = getVenueInfo(event);
    const venueDisplay = <div className="EventItem__VenueInfo">{venueName}</div>;
    const infoDisplay = (
      <div className="EventItem__MixInfo EventItem__MixInfo__Sell">
        <div>
          <time>{timeDisplay}</time> at {venueDisplay}
        </div>
      </div>
    );

    /**
     * If for sell app, multiple events toggle switch is clicked, and toggle is ON, then show checkbox
     * to the left
     *
     *
     * Use cases:
     * On clicking the checkbox, we need to filter events based on performer and venue id and disable those that do not match the criteria
     * and only show those clickable which have the same performer id, venue id and eventType as the one that's clicked
     *
     * Each event item receives an extra field (which is manually set in the controller) called `isDisabled`. If `isDisabled` is true, then we disable the checkbox
     * and remove cursor pointer.
     *
     */
    const eventItemCheckbox =
      isSell && isMultipleEventToggleSelected ? (
        <div className="EventItem__CheckboxContainer" onClick={(e) => e.stopPropagation()}>
          <input
            name="EventItem__MultiEventCheckbox"
            type="checkbox"
            id={`EventItem__MultiEventCheckbox__${event.id}`}
            checked={isMultiCheckboxChecked}
            disabled={isDisabled}
            onChange={(e) => onEventItemCheckboxClick(e.target.checked, event)}
          />
          <label htmlFor={`EventItem__MultiEventCheckbox__${event.id}`} />
        </div>
      ) : null;

    /**
     * Event Item Body Section
     *
     */
    let content = (
      <div
        className={cs(
          'EventItem__Body',
          themeHoverColorClassName(theme, themeProvider),
          isDisabled ? 'EventItem__disabled' : ''
        )}
        ref={(node) => (this.mainNode = node)}
        onClick={(e) => this.navigateTo(e)}
      >
        <div className="EventItem__Info">
          <div className="EventItem__Date">
            {!disableDateStamp ? <DateStamp date={eventDate} className="DateStamp__Sell" /> : null}
          </div>
          <div className="EventItem__Details">
            <div className="EventItem__EventInfo">
              <div className="EventItem__Title" style={{wordWrap: 'break-words'}} onClick={(e) => e.stopPropagation()}>
                {isMultipleEventToggleSelected ? (
                  <div className="EventItem__TitleLink EventItem__TitleSellMultiEvent">
                    <Dotdotdot clamp={2}>{name}</Dotdotdot>
                  </div>
                ) : (
                  <FormattedLink
                    href={sellFlowUrl}
                    className={cs(
                      'EventItem__TitleLink',
                      'EventItem__TitleSellMultiEvent',
                      themeLinkColorClassName(theme, themeProvider)
                    )}
                  >
                    {' '}
                    {/** onClick={(e) => this.navigateToEvent(e)} */}
                    <Dotdotdot clamp={2}>{name}</Dotdotdot>
                  </FormattedLink>
                )}
              </div>
              {infoDisplay}
            </div>
            {eventItemCheckbox}
          </div>
        </div>
      </div>
    );

    return (
      <>
        {triggerUKRedirectModal && <UKRedirectModal />}
        <Panel className={cs(eventItemClassName || 'EventItem')} content={content} border={!hideBorder} />
      </>
    );
  }
}

export default connect({mapStateToProps})(EventItem);
